/* eslint-disable */
// GENERATED FILE — DO NOT EDIT.
//
// A verbatim copy of functions/src/rbac/permissionRefusalCopy.ts, the ONE
// permission-refusal wording (Malaika R7, 2026-09-07). Edit the source and run
// `node scripts/generate-refusal-copy-mirror.mjs`; CI fails on drift.
//
// It exists so the browser can speak the platform's actual refusal instead of
// a second, hand-authored one — the permission room on the public pages
// depends on that being true, and so does anyone who checks it.
//
// The source is pure and import-free, which is why it can cross verbatim.
// Keep it that way: this mirror is on the first-paint path.

/**
 * permissionRefusalCopy — the ONE wording for "your access here doesn't include
 * that" (Malaika rulings 2026-09-07, group-thread roles & permissions series).
 *
 * D9: code may author REFUSAL copy. This module is the inventory for the
 * permission refusal. The shared-worker tool gate (toolRouter) speaks it, the
 * deep-research override surfaces it verbatim, and the planner directive
 * (groupPermissionFloor) hands the same sentence to the model for the cases no
 * tool gate can see — the model writing a letter or a translation itself. One
 * family, so a Read-Only teammate hears the same thing whichever path their
 * request took.
 *
 * The contract, from the rulings:
 *  - direct, second person, one turn: no apology loop, no menu of alternatives;
 *  - names the ASKER's own role when it is known ("You have Read-Only access in
 *    this workspace") — never anyone else's, never "there is a read-only person
 *    in this thread", never an attribution to who else is present;
 *  - says who can run it (anyone here whose role includes it) and that the asker
 *    will be able to read the result in this thread;
 *  - says where the permission is granted (a workspace owner or admin, under
 *    roles & permissions);
 *  - keeps the door open: reading, summarising and asking about work already in
 *    the thread is never gated (owner directive 2026-08-13);
 *  - spoken in the asker's language — Spanish in, Spanish out.
 *
 * A LOOKUP THAT FAILED IS NOT A PERMISSION THE PERSON LACKS. The gate still
 * fails closed (nothing runs), but the copy says access could not be confirmed
 * rather than blaming their role for a Firestore hiccup — the same class of
 * wrong answer as the vague "no case on file" a limited user once got instead
 * of the permission truth.
 *
 * Pure and IO-free.
 */

export type RefusalLanguage = 'en' | 'es';

/**
 * Spanish renderings of the activity labels in the gated set
 * (rbac/groupPermissionFloor GATED_TOOL_CAPABILITIES + the connected-app set).
 * An unmapped label is spoken as-is rather than invented.
 */
const ACTIVITY_LABELS_ES: Readonly<Record<string, string>> = {
  'deep research': 'investigación profunda',
  translation: 'traducción',
  'document drafting': 'redacción de documentos',
  'video script generation': 'generación de guiones de video',
  browsing: 'navegación web',
  'voice calling': 'llamadas de voz',
  'media generation': 'generación de imágenes, video y audio',
  'parallel work': 'trabajo en paralelo',
  'sending email': 'envío de correo electrónico',
  'managing the calendar': 'gestión del calendario',
  'reading connected storage': 'lectura del almacenamiento conectado',
  'editing documents in connected storage': 'edición de documentos en el almacenamiento conectado',
  'organizing connected storage': 'organización del almacenamiento conectado',
  'creating files in connected storage': 'creación de archivos en el almacenamiento conectado',
  'deleting items in connected storage': 'eliminación de elementos en el almacenamiento conectado',
};

/**
 * The five seeded role names in Spanish. A CUSTOM role name is the workspace's
 * own word for itself and is left verbatim.
 */
const SYSTEM_ROLE_NAMES_ES: Readonly<Record<string, string>> = {
  'read-only': 'solo lectura',
  assistant: 'asistente',
  member: 'miembro',
  admin: 'administrador',
  owner: 'propietario',
};

export function activityLabelIn(activityLabel: string, language: RefusalLanguage): string {
  const label = String(activityLabel || '').trim();
  if (language !== 'es') return label;
  return ACTIVITY_LABELS_ES[label.toLowerCase()] || label;
}

/** The asker's primary role name, or '' when nothing usable is known. */
export function primaryRoleName(roleNames?: readonly string[] | null): string {
  for (const raw of roleNames ?? []) {
    const value = String(raw || '').trim();
    if (value) return value;
  }
  return '';
}

export function roleNameIn(roleName: string, language: RefusalLanguage): string {
  const name = String(roleName || '').trim();
  if (language !== 'es') return name;
  return SYSTEM_ROLE_NAMES_ES[name.toLowerCase()] || name;
}

export interface PermissionRefusalInput {
  /** English activity label from the gated set, e.g. "deep research". */
  activityLabel: string;
  /** The ASKER's own role name(s), primary first. Empty/unknown ⇒ role-neutral sentence. */
  roleNames?: readonly string[] | null;
  language?: RefusalLanguage | null;
}

/**
 * The refusal. Role-naming when the role is known, role-neutral otherwise;
 * never a word about anyone else.
 */
export function composePermissionRefusal(input: PermissionRefusalInput): string {
  const language: RefusalLanguage = input.language === 'es' ? 'es' : 'en';
  const activity = activityLabelIn(input.activityLabel, language);
  const role = primaryRoleName(input.roleNames);

  if (language === 'es') {
    const opening = role
      ? `Tienes acceso de ${roleNameIn(role, 'es')} en este espacio de trabajo, y ese acceso no incluye ${activity}, así que no puedo hacer eso por ti.`
      : `Tu acceso en este espacio de trabajo no incluye ${activity}, así que no puedo hacer eso por ti.`;
    return (
      `${opening} Cualquier persona aquí cuyo rol lo incluya puede hacerlo, y podrás leer el ` +
      'resultado en este hilo. Un propietario o administrador del espacio de trabajo puede ' +
      'otorgar ese permiso en roles y permisos. Aún puedes preguntarme sobre cualquier cosa ya ' +
      'producida en este hilo: resúmenes, notas, fuentes o preguntas al respecto están bien.'
    );
  }

  const opening = role
    ? `You have ${role} access in this workspace, which doesn't include ${activity}, so I can't run it for you.`
    : `Your access in this workspace doesn't include ${activity}, so I can't run it for you.`;
  // Kept as whole phrases per line: source-contract tests grep these sentences.
  return (
    `${opening} ` +
    "Anyone here whose role includes it can run it, and you'll be able to read the result in this thread. " +
    'A workspace owner or admin can grant it under roles & permissions. ' +
    'You can still ask me about anything already produced in this thread — summaries, notes, sources, or questions about it are all fine.'
  );
}

/**
 * The fail-closed notice when the permission LOOKUP itself failed: nothing ran,
 * and the person is told their access could not be confirmed — not that their
 * role lacks it.
 */
export function composeAccessUnconfirmedNotice(input: {
  activityLabel: string;
  language?: RefusalLanguage | null;
}): string {
  const language: RefusalLanguage = input.language === 'es' ? 'es' : 'en';
  const activity = activityLabelIn(input.activityLabel, language);
  if (language === 'es') {
    return (
      `No pude confirmar tu acceso en este espacio de trabajo en este momento, así que no ejecuté ` +
      `${activity}. Inténtalo de nuevo en un momento; si sigue ocurriendo, un propietario o ` +
      'administrador del espacio de trabajo puede revisar tu rol en roles y permisos.'
    );
  }
  return (
    `I couldn't confirm your access in this workspace just now, so I haven't run ${activity}. ` +
    'Please try again in a moment; if it keeps happening, a workspace owner or admin can check ' +
    'your role under roles & permissions.'
  );
}

/**
 * Did the permission check say the person LACKS the permission (true), or did
 * it fail to answer at all (false — an unresolvable account, an unavailable
 * store, a tenant-boundary guard)? Only the first earns the role sentence.
 */
export function isMissingPermissionError(err: unknown): boolean {
  const code = String((err as { code?: unknown } | null)?.code ?? '')
    .toLowerCase()
    .replace(/^functions\//, '');
  const message = String((err as { message?: unknown } | null)?.message ?? '');
  if (code === 'permission-denied') return !/boundary violation/i.test(message);
  if (code) return false;
  return /missing required permission|permission denied/i.test(message);
}

/**
 * Spanish-likelihood for the asker's own message. Strong markers first (the
 * same idiom as prest0nSession/stepLimitNarration.detectSpanishLikely: inverted
 * punctuation, ñ, or two accented vowels), then a count of common Spanish
 * function words for the unaccented sentence. Choosing the LANGUAGE of refusal
 * copy is part of authoring refusal copy (D9); it decides no workflow.
 */
const SPANISH_FUNCTION_WORDS =
  /\b(?:el|la|los|las|de|del|que|en|y|una|uno|este|esta|esto|para|por|con|sobre|puedes|necesito|quiero|hazme|genera|generar|redacta|redactar|traduce|traducir|investigación|informe|reporte|documento|carta|favor|gracias)\b/gi;

export function looksSpanishMessage(text: string | null | undefined): boolean {
  const value = String(text || '');
  if (!value.trim()) return false;
  if (/[¿¡ñÑ]/.test(value)) return true;
  if ((value.match(/[áéíóúÁÉÍÓÚ]/g) || []).length >= 2) return true;
  return (value.match(SPANISH_FUNCTION_WORDS) || []).length >= 3;
}

/** The most recent user-authored entry of a conversation history, or ''. */
export function lastUserMessage(
  history?: ReadonlyArray<{ role?: string; content?: unknown }> | null,
): string {
  if (!Array.isArray(history)) return '';
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry && String(entry.role || '') === 'user') {
      return typeof entry.content === 'string' ? entry.content : '';
    }
  }
  return '';
}

/**
 * Which language the refusal is spoken in: the asker's own message decides;
 * with no message to read, the caller's fallback (the account default), else
 * English.
 */
export function pickRefusalLanguage(args: {
  lastUserMessage?: string | null;
  fallback?: string | null;
}): RefusalLanguage {
  const message = String(args.lastUserMessage || '');
  if (message.trim()) return looksSpanishMessage(message) ? 'es' : 'en';
  return args.fallback === 'es' ? 'es' : 'en';
}

/**
 * The decline wording handed to the PLANNER (groupPermissionFloor directive)
 * for the requests no tool gate sees — the model drafting or translating
 * inline. Both languages, `{activity}` left for the model to fill with the
 * asker's own words; the role is filled in when known.
 */
export function composeDeclineWordingForDirective(roleNames?: readonly string[] | null): string {
  const en = composePermissionRefusal({ activityLabel: '{activity}', roleNames, language: 'en' });
  const es = composePermissionRefusal({ activityLabel: '{activity}', roleNames, language: 'es' });
  return `EN: "${en}"\nES: "${es}"`;
}
